import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Activity } from "@/api/queries";
import { useStopContacting } from "@/api/queries";
import { NuevoSeguimiento } from "@/actividades/NuevoSeguimiento";
import { Dialog } from "@/components/Dialog";
import { Button } from "@/components/ui";

/**
 * *¿Qué sigue con este prospecto?* — asked by *Completado*, before anything is
 * saved. A completed follow-up with nothing after it is a prospect that quietly
 * drops out of every agenda, so the follow-up is only marked completed by one
 * of the two answers, and never before it.
 *
 * *Crear seguimiento a futuro* opens the follow-up form limited to the five
 * *a futuro* kinds and a date that has not passed; saving it writes the next
 * follow-up and completes this one in a single request, and cancelling it
 * comes back here rather than out. *Ya no contactar* is a single request too:
 * the company becomes *No interesado* and stays in the seller's book, its
 * timeline records that the person asked not to be contacted, and this
 * follow-up is completed.
 *
 * The ✕ is for the seller who is not ready to say: nothing was saved, so the
 * follow-up is still pending and still on the agenda. Escape does the same; a
 * click beside the dialog does nothing. Only for a follow-up with a company:
 * cold prospecting has no prospect to stop contacting, and closes as it always
 * did.
 */
export function SiguientePaso({
  activity,
  onClose,
}: {
  /** The follow-up being completed. Its `company` is not null. */
  activity: Activity;
  /** Called once an answer has completed the follow-up, or on the ✕. */
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [scheduling, setScheduling] = useState(false);
  const stop = useStopContacting();
  const company = activity.company!;
  const busy = stop.isPending;

  if (scheduling) {
    return (
      <NuevoSeguimiento
        open
        // Cancelar is back to the question, with nothing saved.
        onClose={() => setScheduling(false)}
        onSaved={onClose}
        company={company}
        companyName={activity.company_name ?? ""}
        contact={activity.contact ?? null}
        completes={activity.id}
      />
    );
  }

  return (
    <Dialog
      open
      closeOnBackdrop={false}
      onClose={() => {
        // Not while an answer is being saved: it would be closed half-given.
        if (!busy) onClose();
      }}
      width={560}
      kicker={activity.company_name}
      title={t("nextStep.title")}
    >
      <p className="mb-5 text-sm text-mute-700">{t("nextStep.lead")}</p>

      {stop.isError ? (
        <p role="alert" className="mb-4 border-2 border-brand px-3 py-2 text-sm">
          {stop.error.message}
        </p>
      ) : null}

      <div className="grid gap-3">
        <Button
          variant="primary"
          className="justify-center py-3"
          data-autofocus=""
          disabled={busy}
          onClick={() => setScheduling(true)}
        >
          {t("nextStep.schedule")}
        </Button>
        <Button
          className="justify-center py-3"
          disabled={busy}
          onClick={() => stop.mutate(activity.id, { onSuccess: onClose })}
        >
          {busy ? t("nextStep.saving") : t("nextStep.stop")}
        </Button>
        <p className="text-[12px] text-mute-600">{t("nextStep.stopHint")}</p>
      </div>
    </Dialog>
  );
}
